/**
 * Platform Settings Hook
 * 
 * Provides functionality for managing platform-wide configuration settings.
 * These settings control system-wide behavior and are typically managed by
 * administrators. Settings support multiple data types with validation.
 * 
 * **Key Features:**
 * - Fetch all platform settings or individual settings by key
 * - Admin-only update capabilities with user tracking
 * - Type-safe settings with validation rules (min/max values)
 * - Automatic cache invalidation on updates
 * - Support for number, boolean, string, and JSON data types
 * 
 * **Common Settings:**
 * - Payment processing fees and thresholds
 * - System maintenance windows
 * - Feature flag defaults
 * - API rate limits
 * - Email notification frequencies
 * 
 * **Security:**
 * - Only users with admin role can update settings
 * - All changes are tracked with user ID and timestamp
 * - Settings are validated against defined constraints
 * 
 * @example
 * // Fetch all settings
 * const { data: settings, isLoading } = usePlatformSettings();
 * 
 * @example
 * // Fetch specific setting
 * const { data: paymentFee } = usePlatformSetting('platform_fee_percentage');
 * 
 * @example
 * // Update a setting (admin only)
 * const { mutate: updateSetting } = useUpdatePlatformSetting();
 * 
 * updateSetting({
 *   settingKey: 'max_payment_retry_attempts',
 *   newValue: '5'
 * });
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';

export interface PlatformSetting {
  id: string;
  setting_key: string;
  setting_value: string;
  description: string | null;
  data_type: 'number' | 'boolean' | 'string' | 'json';
  min_value: number | null;
  max_value: number | null;
  updated_by_user_id: string | null;
  created_at: string;
  updated_at: string;
}

// Fetch all platform settings
export const usePlatformSettings = () => {
  return useQuery({
    queryKey: ['platform-settings'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('platform_settings')
        .select('*')
        .order('setting_key');

      if (error) throw error;
      return data as PlatformSetting[];
    },
  });
};

// Fetch a specific setting by key
export const usePlatformSetting = (settingKey: string) => {
  return useQuery({
    queryKey: ['platform-setting', settingKey],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('platform_settings')
        .select('*')
        .eq('setting_key', settingKey)
        .single();

      if (error) throw error;
      return data as PlatformSetting;
    },
    enabled: !!settingKey,
  });
};

// Update a platform setting (admin only)
export const useUpdatePlatformSetting = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ settingKey, newValue }: { settingKey: string; newValue: string }) => {
      const { data: { user } } = await supabase.auth.getUser();
      
      const { data, error } = await supabase
        .from('platform_settings')
        .update({
          setting_value: newValue,
          updated_by_user_id: user?.id,
          updated_at: new Date().toISOString(),
        })
        .eq('setting_key', settingKey)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['platform-settings'] });
      queryClient.invalidateQueries({ queryKey: ['platform-setting', data.setting_key] });
      toast({
        title: 'Success',
        description: 'Platform setting updated successfully',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to update platform setting',
        variant: 'destructive',
      });
    },
  });
};
